/**
 * Practical Example: Using getLeadById to get lead details
 * This shows exactly how to extract project name, user info, dates, and assigned user
 */

import React from 'react';
import { getLeadById, getLeadByPhoneNumber } from '../services/apiService';

// Example 1: Basic usage of getLeadById
export async function getLeadDetails(companyId, leadId) {
  try {
    // Call the API
    const leadData = await getLeadById(companyId, leadId);
    
    console.log('Raw lead data:', leadData);
    
    // Extract all the information you need
    const leadInfo = {
      // Basic lead information
      leadId: leadData.lead_id,
      name: leadData.name || 'N/A',
      email: leadData.email || 'N/A',
      phone: leadData.phones?.[0]?.phone || leadData.phone || 'N/A',
      
      // Project name (check multiple possible fields)
      projectName: getProjectName(leadData),
      
      // Assigned user information
      assignedUser: getAssignedUser(leadData),
      
      // Dates
      createdDate: leadData.created_at ? new Date(leadData.created_at).toLocaleDateString() : 'N/A',
      updatedDate: leadData.updated_at ? new Date(leadData.updated_at).toLocaleDateString() : 'N/A',
      createdDateTime: leadData.created_at ? new Date(leadData.created_at).toLocaleString() : 'N/A',
      
      // Additional info
      status: leadData.status || leadData.lead_status || 'N/A',
      source: leadData.source || leadData.lead_source || 'N/A'
    };
    
    return leadInfo;
    
  } catch (error) {
    console.error('Error fetching lead details:', error);
    throw error;
  }
}

// Helper function to extract project name from multiple possible fields
function getProjectName(leadData) {
  // Check all possible sources for project name
  const sources = [
    leadData.project_name,
    leadData.project?.name,
    leadData.lead_fields?.project_name,
    leadData.lead_fields?.project,
    leadData.campaign_name,
    leadData.campaign?.name
  ];
  
  // Return the first non-empty value
  for (const source of sources) {
    if (source && typeof source === 'string' && source.trim() !== '') {
      return source.trim();
    }
  }
  
  return 'N/A';
}

// Helper function to extract assigned user information
function getAssignedUser(leadData) {
  // Try assigned_to first (most common)
  if (leadData.assigned_to) {
    return {
      id: leadData.assigned_to.id,
      name: leadData.assigned_to.name || 'N/A',
      email: leadData.assigned_to.email || leadData.assigned_to.user_email || 'N/A',
      phone: leadData.assigned_to.user_phone || leadData.assigned_to.phone || 'N/A',
      trackingNumber: leadData.assigned_to.tracking_number || 'N/A'
    };
  }
  
  // Try user_assigned (alternative format)
  if (leadData.user_assigned) {
    return {
      id: leadData.user_assigned.user_id || leadData.user_assigned.id,
      name: leadData.user_assigned.name || 'N/A',
      email: leadData.user_assigned.email_id || leadData.user_assigned.email || 'N/A',
      phone: leadData.user_assigned.phone_number || 'N/A',
      trackingNumber: leadData.user_assigned.tracking_number || 'N/A'
    };
  }
  
  // Fallback to custom fields
  const customFields = leadData.lead_fields || {};
  const managerName = customFields.sourcing_manager_5fd || 
                     customFields.closing_manager_cb3 || 
                     customFields.platform__copy__797;
  
  if (managerName) {
    return {
      id: 'N/A',
      name: Array.isArray(managerName) ? managerName[0] : managerName,
      email: 'N/A',
      phone: 'N/A',
      trackingNumber: 'N/A'
    };
  }
  
  return {
    id: 'N/A',
    name: 'Not Assigned',
    email: 'N/A',
    phone: 'N/A',
    trackingNumber: 'N/A'
  };
}

// Example 2: Search leads by phone and get details
export async function searchAndGetLeadDetails(companyId, phoneNumber) {
  try {
    // First search by phone number
    const searchResponse = await getLeadByPhoneNumber(companyId, phoneNumber);
    
    if (!searchResponse.data || searchResponse.data.length === 0) {
      return { found: false, leads: [] };
    }
    
    const leads = searchResponse.data;
    console.log(`Found ${leads.length} leads with phone ${phoneNumber}`);
    
    // Get detailed information for each lead
    const detailedLeads = [];
    
    for (const lead of leads) {
      try {
        const details = await getLeadDetails(companyId, lead.lead_id);
        detailedLeads.push(details);
      } catch (error) {
        console.error(`Error getting details for lead ${lead.lead_id}:`, error);
        // Add basic info even if detailed fetch fails
        detailedLeads.push({
          leadId: lead.lead_id,
          name: lead.name || 'N/A',
          email: lead.email || 'N/A',
          phone: phoneNumber,
          projectName: getProjectName(lead),
          assignedUser: getAssignedUser(lead),
          createdDate: lead.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A',
          status: lead.status || 'N/A',
          source: lead.source || 'N/A'
        });
      }
    }
    
    return {
      found: true,
      count: detailedLeads.length,
      leads: detailedLeads
    };
    
  } catch (error) {
    console.error('Error searching leads:', error);
    throw error;
  }
}

// Example 3: React Hook for using lead data
export function useLeadDetails(companyId, leadId) {
  const [leadData, setLeadData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  React.useEffect(() => {
    if (!companyId || !leadId) return;
    
    const fetchLead = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const details = await getLeadDetails(companyId, leadId);
        setLeadData(details);
        
      } catch (err) {
        setError(err.message);
        console.error('Error in useLeadDetails:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLead();
  }, [companyId, leadId]);
  
  return { leadData, loading, error };
}

// Example 4: Complete React Component
export const LeadDetailsDisplay = ({ companyId, leadId }) => {
  const { leadData, loading, error } = useLeadDetails(companyId, leadId);
  
  if (loading) return <div>Loading lead details...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!leadData) return <div>No lead found</div>;
  
  return (
    <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
      <h3>Lead Details</h3>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Lead ID:</strong> {leadData.leadId}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Name:</strong> {leadData.name}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Email:</strong> {leadData.email}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Phone:</strong> {leadData.phone}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Project Name:</strong> {leadData.projectName}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Assigned User:</strong> {leadData.assignedUser.name}
        {leadData.assignedUser.email !== 'N/A' && (
          <span> ({leadData.assignedUser.email})</span>
        )}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Created Date:</strong> {leadData.createdDate}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Last Updated:</strong> {leadData.updatedDate}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Status:</strong> {leadData.status}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Source:</strong> {leadData.source}
      </div>
    </div>
  );
};

// Example 5: Usage in your existing component
export const ExampleUsageInComponent = `
// In your React component:

import { getLeadDetails, searchAndGetLeadDetails } from './LeadUsageExample';

const MyComponent = () => {
  const [leadInfo, setLeadInfo] = useState(null);
  
  // Method 1: Get lead by ID
  const fetchLeadById = async (companyId, leadId) => {
    try {
      const details = await getLeadDetails(companyId, leadId);
      console.log('Lead details:', details);
      
      // Now you have:
      console.log('Project Name:', details.projectName);
      console.log('Assigned User:', details.assignedUser.name);
      console.log('Created Date:', details.createdDate);
      console.log('Email:', details.email);
      
      setLeadInfo(details);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  // Method 2: Search by phone and get details
  const searchByPhone = async (companyId, phone) => {
    try {
      const result = await searchAndGetLeadDetails(companyId, phone);
      
      if (result.found) {
        console.log(\`Found \${result.count} leads\`);
        result.leads.forEach(lead => {
          console.log('Lead:', lead.name, 'Project:', lead.projectName);
        });
      } else {
        console.log('No leads found');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  return (
    <div>
      <button onClick={() => fetchLeadById('company123', 'lead456')}>
        Get Lead Details
      </button>
      
      <button onClick={() => searchByPhone('company123', '1234567890')}>
        Search by Phone
      </button>
      
      {leadInfo && (
        <div>
          <h3>{leadInfo.name}</h3>
          <p>Project: {leadInfo.projectName}</p>
          <p>Manager: {leadInfo.assignedUser.name}</p>
          <p>Created: {leadInfo.createdDate}</p>
        </div>
      )}
    </div>
  );
};
`;

// Export the helper functions for use in other components
export { getProjectName, getAssignedUser };